// Pure, DOM-free helpers for the Document Shape Telemetry views — split out
// so the two things most worth getting exactly right here are independently
// testable: (1) which of the five compliance states a raw wire value maps
// to, and (2) how much weight a jurisdiction's observation count deserves
// before its percentage is trusted. Mirrors the split in
// app-analytics-empty-state.ts.
//
// The one rule that must never break, per document-profile-contract.md:
// ABSENT (the DDA element wasn't present at all) and 'N' (the issuer
// explicitly asserted non-compliant) are different facts. They must never
// render identically or be summed together anywhere in this feature.

import type { ComplianceState } from '../api/document-profiles';
export type { ComplianceState };

/**
 * Classify a raw complianceIndicator wire value — as stored: `null`, a
 * single A-Z letter, or the literal string `"OTHER"` — into one of the five
 * states this UI must always keep visually distinct. `null` and the literal
 * `"OTHER"` are different inputs that both land on their own state (ABSENT,
 * OTHER respectively); every other single letter besides F/N/M also lands
 * on OTHER, since AAMVA only defines those three.
 */
export function classifyCompliance(raw: string | null): ComplianceState {
  if (raw === null) return 'ABSENT';
  if (raw === 'F' || raw === 'N' || raw === 'M') return raw;
  return 'OTHER';
}

export const COMPLIANCE_LABELS: Record<ComplianceState, string> = {
  ABSENT: 'Absent',
  F: 'F — Compliant',
  N: 'N — Not compliant',
  M: 'M — Materially compliant',
  OTHER: 'Other',
};

// One pill class per state — five genuinely different treatments, not
// shades of the same one, so no two states read as "basically the same" at
// a glance. See styles.css for pill-absent/pill-info/pill-other.
export const COMPLIANCE_PILL_CLASS: Record<ComplianceState, string> = {
  ABSENT: 'pill pill-absent',
  F: 'pill pill-ok',
  N: 'pill pill-warn',
  M: 'pill pill-info',
  OTHER: 'pill pill-other',
};

// Display order for the five states wherever they appear as columns —
// ABSENT first since it's the one most likely to be mistaken for a zero.
export const COMPLIANCE_STATE_ORDER: ComplianceState[] = ['ABSENT', 'F', 'N', 'M', 'OTHER'];

export type SampleConfidence = 'insufficient' | 'thin' | 'ok';

// Judgment calls, not a spec: under 5 observations a percentage can flip 20+
// points on the very next scan, so it's suppressed rather than shown; under
// 30 it's real but easy to over-read as settled, so it's shown dimmed with
// the raw count alongside it rather than hidden.
const INSUFFICIENT_BELOW = 5;
const THIN_BELOW = 30;

export function sampleConfidence(n: number): SampleConfidence {
  if (n < INSUFFICIENT_BELOW) return 'insufficient';
  if (n < THIN_BELOW) return 'thin';
  return 'ok';
}

/** Row CSS class for a table row whose weight comes entirely from `n`. */
export function sampleRowClass(n: number): string {
  const c = sampleConfidence(n);
  if (c === 'insufficient') return 'row-sparse';
  if (c === 'thin') return 'row-thin';
  return '';
}

// ── Coverage matrix pivot ────────────────────────────────────────────────

export interface CoverageMatrixCellInput {
  jurisdiction: string;
  aamvaVersion: number | null;
  complianceState: ComplianceState;
  observations: number;
}

export interface CoverageMatrixRow {
  jurisdiction: string;
  aamvaVersion: number | null;
  total: number;
  byState: Record<ComplianceState, number>;
}

/**
 * Pivot flat (jurisdiction, aamvaVersion, complianceState) cells into one
 * row per (jurisdiction, aamvaVersion) with all five states as columns —
 * including states that had zero observations, so a missing state reads as
 * "zero", never as a missing table cell that could be misread as "unknown".
 */
export function pivotCoverageMatrix(
  cells: CoverageMatrixCellInput[],
): CoverageMatrixRow[] {
  const rows = new Map<string, CoverageMatrixRow>();
  for (const cell of cells) {
    const key = `${cell.jurisdiction} ${cell.aamvaVersion ?? ''}`;
    let row = rows.get(key);
    if (!row) {
      row = {
        jurisdiction: cell.jurisdiction,
        aamvaVersion: cell.aamvaVersion,
        total: 0,
        byState: { ABSENT: 0, F: 0, N: 0, M: 0, OTHER: 0 },
      };
      rows.set(key, row);
    }
    row.byState[cell.complianceState] += cell.observations;
    row.total += cell.observations;
  }
  return [...rows.values()];
}
